import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import AddIcon from "@mui/icons-material/Add";
import Grid from "@mui/material/Unstable_Grid2/Grid2";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import { Button, Toolbar, Tooltip, Typography } from "@mui/material";

import { HorizontalStack } from "../components/primitives/Stacks";
import { NewPatientCard, PatientCard } from "../components/PatientCard";
import { Page } from "../components/layout/Page";
import { Patient } from "../models/patient";
import { PatientInfoDialog } from "../components/PatientInfoDialog";
import { PatientMenu } from "../components/PatientMenu";
import { Spacer } from "../components/primitives/Spacer";
import { useAppContext } from "../context/AppContext";
import { usePatients } from "../queries/patients-query";

export const PatientsPage: React.FC<{ patients: Patient[] }> = ({
  patients,
}) => {
  const { t } = useTranslation();

  const { togglePatientDialog } = useAppContext();

  const patientsElements = useMemo(() => {
    return patients.map((patient: Patient) => (
      <Grid xs={12} sm={6} md={4} lg={3} key={patient.id}>
        <PatientCard patient={patient} />
      </Grid>
    ));
  }, [patients]);

  return (
    <>
      <Page HeadProps={{ title: `${t("patients.title")}` }}>
        <Toolbar disableGutters sx={{ display: { xs: "none", md: "flex" } }}>
          <HorizontalStack sx={{ flexGrow: 1 }} alignItems="center">
            <GroupOutlinedIcon sx={{ color: "text.secondary" }} />
            <Typography variant="h2">
              {t("patients.count", { count: patients.length || 0 })}
            </Typography>
          </HorizontalStack>
          <HorizontalStack>
            <Tooltip title={t("patient.create-tip")}>
              <Button
                onClick={() => togglePatientDialog()}
                startIcon={<AddIcon />}
                variant="outlined"
              >
                {t("patient.create-cta")}
              </Button>
            </Tooltip>
          </HorizontalStack>
        </Toolbar>
        <Spacer sx={{ display: { xs: "none", md: "flex" } }} />
        <Grid container>
          {patientsElements}
          <Grid xs={12} sm={6} md={4} lg={3}>
            <NewPatientCard />
          </Grid>
        </Grid>
      </Page>
      <PatientInfoDialog />
      <PatientMenu />
    </>
  );
};

const ConnectedPatientsPage: React.FC = () => {
  const { data: patients } = usePatients();

  if (!patients) return;

  return <PatientsPage patients={patients} />;
};

export default ConnectedPatientsPage;
